import { useEffect, useState } from 'react';
import * as api from '../../api/client';
import { Alert } from '../common/Alert';
import { Button } from '../common/Button';
import { Card } from '../common/Card';
import { apiError } from '../../utils/apiError';
import { describeUserAgent } from '../../utils/userAgent';
import { formatDateTime } from '../../utils/datetime';
import { pushSupport, permissionState, currentSubscription, subscribe, unsubscribe } from '../../utils/push';
import { PushSubscriptionInfo } from '../../types';

/**
 * Opt this device in to Web Push. Which events reach it is decided by a
 * "Browser push" notification channel (Notifications page), so routing works
 * the same way as Slack or email.
 */
export function BrowserPushSection() {
  const support = pushSupport();
  const [permission, setPermission] = useState(permissionState());
  const [endpoint, setEndpoint] = useState<string | null>(null);
  const [devices, setDevices] = useState<PushSubscriptionInfo[]>([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  async function refresh() {
    try {
      const [sub, list] = await Promise.all([currentSubscription(), api.getPushSubscriptions()]);
      setEndpoint(sub?.endpoint ?? null);
      setDevices(list);
    } catch {
      // Unsupported browsers land here; the copy below explains.
    }
  }

  useEffect(() => { if (support === 'supported') refresh(); }, [support]);

  const subscribedHere = endpoint !== null && devices.some(d => d.endpoint === endpoint);

  async function toggle() {
    setBusy(true); setMsg(''); setErr('');
    try {
      if (subscribedHere) {
        await unsubscribe();
        setMsg('This device will no longer receive notifications.');
      } else {
        await subscribe();
        setMsg('This device is subscribed. Add a “Browser push” notification channel to choose which events reach it.');
      }
      setPermission(permissionState());
      await refresh();
    } catch (e) {
      setErr(apiError(e, e instanceof Error ? e.message : 'Could not change the subscription'));
      setPermission(permissionState());
    } finally {
      setBusy(false);
    }
  }

  async function sendTest() {
    setBusy(true); setMsg(''); setErr('');
    try {
      const r = await api.testBrowserPush();
      setMsg(r.sent > 0 ? `Test sent to ${r.sent} device${r.sent === 1 ? '' : 's'}.` : 'No device accepted the test notification.');
    } catch (e) {
      setErr(apiError(e, 'Could not send a test'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="p-6">
      <h2 className="text-lg font-semibold mb-1">Browser notifications</h2>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
        Native notifications on this device, even with the tab closed. A “Browser push” notification channel decides which events reach subscribed devices.
      </p>

      {msg && <Alert tone="success">{msg}</Alert>}
      {err && <Alert tone="error">{err}</Alert>}

      {support === 'unsupported' && (
        <p className="text-sm text-gray-500 dark:text-gray-400">This browser does not support Web Push.</p>
      )}
      {support === 'insecure-context' && (
        <p className="text-sm text-gray-500 dark:text-gray-400">Browser notifications need HTTPS (or localhost). Open PulseCheck over HTTPS to enable them.</p>
      )}
      {support === 'supported' && (
        <>
          {permission === 'denied' && (
            <Alert tone="error">Notifications are blocked for this site in the browser. Allow them in the site settings, then try again.</Alert>
          )}
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <Button onClick={toggle} loading={busy} disabled={permission === 'denied'} variant={subscribedHere ? 'secondary' : 'primary'}>
              {subscribedHere ? 'Turn off on this device' : 'Enable on this device'}
            </Button>
            {devices.length > 0 && (
              <Button variant="ghost" onClick={sendTest} loading={busy}>Send test</Button>
            )}
          </div>

          {devices.length > 0 && (
            <ul className="mt-4 space-y-2">
              {devices.map(d => (
                <li key={d.id} className="flex items-center justify-between gap-3 p-3 bg-gray-50 dark:bg-gray-900/50 rounded-md text-sm">
                  <div className="min-w-0">
                    <p className="font-medium truncate">
                      {describeUserAgent(d.user_agent)}
                      {d.endpoint === endpoint && <span className="ml-2 text-xs font-normal text-brand-600 dark:text-brand-400">this device</span>}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                      subscribed {formatDateTime(d.created_at)}{d.last_used_at ? ` · last push ${formatDateTime(d.last_used_at)}` : ''}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </Card>
  );
}
